// eslint-disable-next-line import/no-extraneous-dependencies
import { useEffect, useState } from 'react';
import Hero from '../../components/Hero';
import Layout from '../../components/Layout';
import Section from '../../components/Section';
import CampaignCard from '../../components/cards/CampaignCard';
import { accessoriesPageQuery } from '../../groq/pages/accessories';
import { sanityClient } from '../../lib/sanity.server';
import {
  AccessoryType,
  CampaignSingularType,
  DefaultSEOType,
  ImageWithAltType,
  MenuType,
  SEOType,
} from '../../types';
import fetchSanityData from '../../utils/fetchSanityData';

interface PageProps {
  data: {
    menus: MenuType;
    content: {
      overline?: string;
      title: string;
      description?: string;
      image?: ImageWithAltType;
      accessories: AccessoryType[];
      allTags: {
        overline?: string;
        tagName: string;
        slug: string;
        description: string;
        image?: ImageWithAltType;
      }[];
      tags: string[];
      seo?: SEOType;
      defaultSEO: DefaultSEOType;
    };
  };
}

function AccessoriesPage({ data }: PageProps) {
  const {
    menus,
    content: { title, description, tags, allTags, image: topImage, seo, defaultSEO, overline },
  } = data;

  const [campaigns, setCampaigns] = useState<CampaignSingularType[] | undefined>(undefined);

  useEffect(() => {
    const fetchCampaigns: () => void = async () => {
      const response = await fetchSanityData('accessoriesPage');
      setCampaigns(response.campaigns);
    };

    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reducedTags = [...new Set(tags)].filter((tag) => tag !== 'alle');
  const displayTags = allTags
    .filter((tag) => reducedTags.includes(tag.slug))
    .sort((a, b) => (a.tagName.toLowerCase() > b.tagName.toLowerCase() ? 1 : -1));

  return (
    <Layout menus={menus} seo={seo} defaultSEO={defaultSEO}>
      <Hero
        title={title}
        description={description}
        image={topImage}
        overline={overline}
        background="primary"
      />

      <Section>
        <div className="flex flex-col gap-20">
          {Array.isArray(campaigns) && !!campaigns.length && (
            <div>
              <h2 className="mb-5 text-3xl font-semibold lg:mb-10 lg:text-4xl">Kampanjer</h2>
              <div className="grid gap-5">
                {campaigns.map((campaign) => {
                  return (
                    <CampaignCard
                      key={campaign._id}
                      overline={campaign.overline}
                      title={campaign.title}
                      description={campaign.pitch}
                      image={campaign.image}
                      href={`${encodeURI(campaign.href || '')}`}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {Array.isArray(displayTags) && !!displayTags.length && (
            <div>
              <h2 className="mb-5 text-3xl font-semibold lg:mb-10 lg:text-4xl">
                Vårt utvalg av tilbehør
              </h2>
              <div className="grid gap-5">
                {displayTags.map((tag) => {
                  const {
                    tagName,
                    description: tagDescription,
                    image,
                    slug,
                    overline: tagOverline,
                  } = tag;
                  if (!image) return null;
                  return (
                    <CampaignCard
                      overline={tagOverline}
                      key={tagName}
                      title={tagName}
                      description={tagDescription}
                      image={image}
                      href={`/tilbehor/${slug}`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Section>
    </Layout>
  );
}

export const getStaticProps = async () => {
  const data = await sanityClient.fetch(accessoriesPageQuery);

  return {
    props: {
      data,
    },
    revalidate: 1,
  };
};
export default AccessoriesPage;
